import cn from './cn.js';

/**
 * Tabs
 * tabs: [{ value, label, count, icon, badge, disabled, hidden }]
 * variant: "underline" (default) | "pills" (segmented control)
 */
export default function Tabs({ tabs, value, onChange, variant = 'underline', size = 'md', className, ariaLabel = 'Tabs' }) {
  const visible = tabs.filter((tab) => !tab.hidden);

  if (variant === 'pills') {
    return (
      <div
        role="tablist"
        aria-label={ariaLabel}
        className={cn('inline-flex max-w-full items-center gap-1 overflow-x-auto no-scrollbar rounded-lg bg-gray-100 p-1', className)}
      >
        {visible.map((tab) => {
          const active = tab.value === value;
          const Icon = tab.icon;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              disabled={tab.disabled}
              onClick={() => onChange(tab.value)}
              className={cn(
                'inline-flex items-center gap-1.5 whitespace-nowrap rounded-md font-medium transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50',
                size === 'sm' ? 'h-7 px-2.5 text-xs' : 'h-8 px-3 text-sm',
                active ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-gray-600 hover:text-gray-900',
              )}
            >
              {Icon && <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />}
              {tab.label}
              {tab.count != null && (
                <span className={cn('rounded-full px-1.5 text-2xs font-semibold', active ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-200 text-gray-600')}>
                  {tab.count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={cn('border-b border-gray-200', className)}>
      <nav role="tablist" aria-label={ariaLabel} className="-mb-px flex gap-x-6 overflow-x-auto no-scrollbar">
        {visible.map((tab) => {
          const active = tab.value === value;
          const Icon = tab.icon;
          return (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={active}
              aria-current={active ? 'page' : undefined}
              disabled={tab.disabled}
              onClick={() => onChange(tab.value)}
              className={cn(
                'group inline-flex shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-1 font-medium transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 disabled:cursor-not-allowed disabled:opacity-50',
                size === 'sm' ? 'py-2.5 text-xs' : 'py-3.5 text-sm',
                active ? 'border-indigo-600 text-indigo-600' : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700',
              )}
            >
              {Icon && <Icon className={cn('h-4 w-4 shrink-0', active ? 'text-indigo-600' : 'text-gray-400 group-hover:text-gray-500')} aria-hidden="true" />}
              {tab.label}
              {tab.count != null && (
                <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', active ? 'bg-indigo-50 text-indigo-700' : 'bg-gray-100 text-gray-600')}>
                  {tab.count}
                </span>
              )}
              {tab.badge && (
                <span className="rounded-md bg-indigo-50 px-1.5 py-0.5 text-2xs font-semibold uppercase tracking-wide text-indigo-700">{tab.badge}</span>
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}
